import type { createReportRequest } from '../requests/report.ts';

export type DuplicateReportPayload = Parameters<typeof createReportRequest>[0];

/** Metadata keys that describe the source's lifecycle rather than its content. */
const LIFECYCLE_METADATA_KEYS = ['publishedAt', 'publishedCompanyId', 'archivedAt'] as const;

// Report records are WarpDrive proxies, which structuredClone rejects; both
// fields are JSON columns, so a JSON round trip is lossless.
function jsonCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value ?? {})) as T;
}

/**
 * Builds the create-report payload for a copy of a saved report.
 *
 * The copy keeps the source's report type, description, search config and
 * content metadata (deep-copied, so editing the copy never touches the source).
 * It starts private and unpublished: publish and archive metadata are dropped,
 * `scope` is reset to `admin_only`, and `saved_at` is stamped. Schedules and the
 * internal publish channel live outside the record and are not copied.
 *
 * @param source the saved report to copy.
 * @param options.companyId when set, the copy's partner access is locked to
 *   this company, matching how saves are locked on company-scoped pages;
 *   otherwise the copy is created with no partner access.
 * @param options.now the creation time, stamped as `metadata.saved_at`.
 * @returns the attributes for `createReportRequest`.
 */
export function buildDuplicateReportPayload(
  source: {
    name?: string | null;
    description?: string | null;
    reportType: string;
    searchConfig?: Record<string, unknown> | null;
    metadata?: Record<string, unknown> | null;
  },
  options: { companyId?: string | null; now?: Date } = {}
): DuplicateReportPayload {
  const metadata = jsonCopy(source.metadata ?? {});
  for (const key of LIFECYCLE_METADATA_KEYS) delete metadata[key];

  const companyId = options.companyId ? Number(options.companyId) : null;

  return {
    name: duplicateReportName(source.name),
    description: source.description ?? '',
    reportType: source.reportType,
    searchConfig: jsonCopy(source.searchConfig ?? {}),
    visibilityLevel: 'private',
    partnerAccessConfig: companyId ? [companyId] : [],
    metadata: {
      ...metadata,
      scope: 'admin_only',
      saved_at: (options.now ?? new Date()).toISOString(),
    },
  };
}

/**
 * The default name for a copy.
 *
 * @param name the source report's name; blank names fall back to "Untitled report".
 * @returns the name with " (Copy)" appended.
 */
export function duplicateReportName(name: string | null | undefined): string {
  return `${name?.trim() || 'Untitled report'} (Copy)`;
}
